import * as React from 'react';
import { HealthMetricDeserialisedDataV3 } from '@leagueplatform/dashboard-api';
import { LineGraph } from '#components/charts/core/line-graph/index.ts';
import {
  CHART_VIEW,
  CHART_VIEWS,
} from '#components/charts/constants/chart-views.ts';
import { RangeGraph } from '#components/charts/core/range-graph/index.ts';
import { useHealthMetricValuesV3 } from '#components/charts/core/range-graph/utils/use-health-metric-values.ts';
import { shapes } from '#components/charts/core/xychart/primitives/Shape.tsx';
import { useIntl } from '@leagueplatform/locales';
import { BarGraph } from '#components/charts/core/bar-graph/index.ts';
import { MultipleMeasurementsTooltipV3 } from '#components/charts/core/chart-tooltip/v3/multiple-measurements-tooltipV3.tsx';
import { SingleMeasurementTooltipV3 } from '#components/charts/core/chart-tooltip/v3/single-measurement-tooltipV3.tsx';
import { useDatumLabel } from '../hooks/use-datum-label';

function getHighestValueFromEnums(chartData: ChartProps['chartData']) {
  const enumOptionValues: number[] = [];
  chartData.addInputConfig?.data.healthMetricInputs.forEach((input) =>
    input.valueFields.forEach((valueField) =>
      valueField.options?.forEach((option) =>
        enumOptionValues.push(option.value),
      ),
    ),
  );
  const highestEnumValue = Math.max(...enumOptionValues);
  return highestEnumValue;
}

type ChartProps = {
  chartView: CHART_VIEW;
  chartData: HealthMetricDeserialisedDataV3['data'][number];
};

export function Chart({ chartView, chartData }: ChartProps) {
  const { $tifelse } = useIntl();
  const { screenReaderTitle, screenReaderDescription } = chartData;
  const axisLeftLabel = chartData.axisLeftLabel.toLocaleUpperCase();
  const axisBottomLabel = $tifelse(
    chartView === CHART_VIEWS.WEEKLY,
    'DAY_OF_THE_WEEK',
    'DAY_OF_THE_MONTH',
  );
  const generateDatumLabel = useDatumLabel();

  const data = useHealthMetricValuesV3(chartData.values.data);
  const highestEnumValue = getHighestValueFromEnums(chartData);

  switch (chartData.chartType) {
    case 'bar': {
      return (
        <BarGraph
          chartView={chartView}
          data={data}
          accessors={{
            xAccessor: (datum) => datum.timestamp,
            yAccessor: (datum) => datum.value as number,
          }}
          title={screenReaderTitle}
          description={screenReaderDescription}
          axisLeftLabel={axisLeftLabel}
          axisBottomLabel={axisBottomLabel}
          ariaDatumLabel={generateDatumLabel}
          renderTooltip={(datum) => (
            <SingleMeasurementTooltipV3 datum={datum} interval={chartView} />
          )}
          yMaxFromEnums={highestEnumValue}
        />
      );
    }
    case 'line': {
      return (
        <LineGraph
          chartView={chartView}
          data={data}
          accessors={{
            xAccessor: (datum) => datum.timestamp,
            yAccessor: (datum) => datum.value as number,
          }}
          title={screenReaderTitle}
          description={screenReaderDescription}
          axisLeftLabel={axisLeftLabel}
          axisBottomLabel={axisBottomLabel}
          ariaDatumLabel={generateDatumLabel}
          renderTooltip={(datum) => (
            <SingleMeasurementTooltipV3 datum={datum} interval={chartView} />
          )}
          yMaxFromEnums={highestEnumValue}
        />
      );
    }
    case 'range': {
      return (
        <RangeGraph
          chartView={chartView}
          data={data}
          accessors={{
            xAccessor: (datum) => datum.timestamp,
            yAccessor: (datum) => datum.value,
          }}
          title={screenReaderTitle}
          description={screenReaderDescription}
          axisLeftLabel={axisLeftLabel}
          axisBottomLabel={axisBottomLabel}
          ariaDatumLabel={generateDatumLabel}
          getShape={(datum) => shapes[datum.metric.data.order]}
          renderTooltip={(datum) => (
            <MultipleMeasurementsTooltipV3 data={datum} interval={chartView} />
          )}
          yMaxFromEnums={highestEnumValue}
        />
      );
    }
    default:
      return null;
  }
}
